import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProfile, getSkills, getEducation, getExperience } from "../api/profileAPI";
import { getPublishedPosts } from "../api/blogAPI";
import { fetchCertificates } from "../api/certificateAPI";
import { fetchPortofolios } from "../api/portofolioAPI";
import { trackVisit } from "../api/analyticsAPI";
import ExperienceMediaCards from "../components/ExperienceMediaCards";
import { sortExperience, calcDuration } from "../utils/sortExperience";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

// ─── Navbar ───────────────────────────────────────────────────────────────────
const NAV_ITEMS = ["home", "about", "skills", "experience", "education", "portfolio", "certificates", "blog", "contact"];

const Navbar = ({ activeSection }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur border-b border-green-500/20 font-mono">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <span className="text-sm text-green-400 font-bold">
          <span className="text-green-600">root@</span>affan
          <span className="text-green-600">:~$</span>
          <span className="ml-1 inline-block w-2 h-4 bg-green-400 align-middle animate-pulse" />
        </span>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item)}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors duration-150 border-b-2 ${
                activeSection === item
                  ? "text-green-400 border-green-500"
                  : "text-green-700 border-transparent hover:text-green-400"
              }`}
            >
              <span className="text-green-800">./</span>{item}
            </button>
          ))}
        </nav>

        <button className="md:hidden text-green-500 p-2" onClick={() => setMenuOpen((v) => !v)}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden bg-black border-b border-green-500/20 px-4 pb-3 font-mono">
          {NAV_ITEMS.map((item) => (
            <button key={item} onClick={() => scrollTo(item)}
              className="block w-full text-left px-3 py-2.5 text-xs text-green-600 hover:text-green-400 transition-colors">
              <span className="text-green-800">./</span>{item}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

// ─── Window chrome wrapper ────────────────────────────────────────────────────
const TermWindow = ({ title, children, className = "" }) => (
  <div className={`rounded-lg border border-green-500/30 bg-[#0a0e0a] overflow-hidden shadow-[0_0_30px_rgba(34,197,94,0.06)] ${className}`}>
    <div className="flex items-center gap-2 px-3 py-2 bg-[#0d130d] border-b border-green-500/20">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
      <span className="ml-2 text-[11px] text-green-700 font-mono truncate">{title}</span>
    </div>
    <div className="p-4 sm:p-5 font-mono">{children}</div>
  </div>
);

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ cmd }) => (
  <div className="mb-8 font-mono">
    <p className="text-sm text-green-500">
      <span className="text-green-700">affan@portfolio</span>
      <span className="text-green-800">:~$ </span>
      <span className="text-green-300">{cmd}</span>
    </p>
  </div>
);

// ─── Hero ─────────────────────────────────────────────────────────────────────
const HeroSection = ({ profile }) => {
  const name = profile?.full_name || "Affan Haidar Atstsabit";
  const headline = profile?.headline || "NOC Engineer · Cybersecurity Enthusiast";
  const photo = resolveImg(profile?.photo_url);

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-14">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 w-full">
        <TermWindow title="bash — affan@portfolio: ~/whoami">
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-1 min-w-0">
              <p className="text-green-600 text-sm">$ whoami</p>
              <h1 className="text-2xl sm:text-4xl font-black text-green-400 mt-1 mb-1 leading-tight tracking-tight">
                {name}
              </h1>
              <p className="text-green-600 text-sm mb-4">
                <span className="text-green-800">{"// "}</span>{headline}
              </p>

              <p className="text-green-600 text-sm mt-4">$ cat status.txt</p>
              <p className="text-green-300 text-sm flex items-center gap-2 mb-5">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
                Available for opportunities — open to work
              </p>

              <p className="text-green-600 text-sm">$ ls ./actions</p>
              <div className="flex flex-wrap gap-3 mt-2">
                <button
                  onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
                  className="px-4 py-2 bg-green-500/10 border border-green-500/50 hover:bg-green-500/20 hover:border-green-400 text-green-400 font-bold rounded text-xs transition-all duration-150"
                >
                  ./explore_work.sh
                </button>
                {profile?.cv_pdf_url && (
                  <a
                    href={resolveImg(profile.cv_pdf_url)}
                    download
                    className="px-4 py-2 bg-transparent border border-green-700 hover:border-green-500 text-green-500 hover:text-green-400 font-bold rounded text-xs transition-all duration-150"
                  >
                    wget cv.pdf
                  </a>
                )}
                {profile?.cv_docx_url && (
                  <a
                    href={resolveImg(profile.cv_docx_url)}
                    download
                    className="px-4 py-2 bg-transparent border border-green-700 hover:border-green-500 text-green-500 hover:text-green-400 font-bold rounded text-xs transition-all duration-150"
                  >
                    wget cv.docx
                  </a>
                )}
              </div>

              <div className="flex gap-2 mt-6">
                {[
                  { href: profile?.show_linkedin ? profile?.social_linkedin : null, label: "linkedin", d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
                  { href: profile?.show_github ? profile?.social_github : null, label: "github", d: "M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" },
                  { href: profile?.show_instagram ? profile?.social_instagram : null, label: "instagram", d: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" },
                ].filter((s) => s.href).map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" title={s.label}
                    className="w-9 h-9 rounded border border-green-700 hover:border-green-400 hover:bg-green-500/10 flex items-center justify-center text-green-600 hover:text-green-400 transition-all duration-150">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d={s.d} /></svg>
                  </a>
                ))}
              </div>
            </div>

            {photo && (
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="relative w-40 h-48 rounded border border-green-500/40 overflow-hidden grayscale contrast-125 hover:grayscale-0 transition-all duration-300">
                  <img src={photo} alt={name} className="w-full h-full object-cover mix-blend-luminosity hover:mix-blend-normal" />
                  <div className="absolute inset-0 bg-green-500/10 pointer-events-none" />
                </div>
                <p className="text-[10px] text-green-700 mt-1 text-center">user.jpg</p>
              </div>
            )}
          </div>
        </TermWindow>
      </div>
    </section>
  );
};

// ─── About ────────────────────────────────────────────────────────────────────
const AboutSection = ({ profile }) => {
  const bio = profile?.description || "Cybersecurity-oriented engineer with a strong foundation in network operations.";
  const doingNow = profile?.doing_now || "Cybersecurity · NOC Engineering · Full-Stack Development";

  return (
    <section id="about" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHeader cmd="cat about.md" />
        <TermWindow title="about.md">
          <p className="text-green-300 text-sm leading-relaxed whitespace-pre-wrap">
            <span className="text-green-700"># </span>About
            {"\n\n"}{bio}
          </p>
          {doingNow && (
            <p className="text-green-500 text-sm mt-4">
              <span className="text-green-700">$ </span>echo $CURRENT_FOCUS
              <br />
              <span className="text-green-300">&gt; {doingNow}</span>
            </p>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-5 text-xs">
            {[
              profile?.show_email && profile?.social_email ? ["email", profile.social_email] : null,
              ["birthday", "2000-10-07"],
              ["location", "South Tangerang, ID"],
              ["status", "open_to_work"],
            ].filter(Boolean).map(([k, v]) => (
              <p key={k} className="text-green-600 truncate">
                <span className="text-green-800">{k}:</span> <span className="text-green-300">{v}</span>
              </p>
            ))}
          </div>
        </TermWindow>
      </div>
    </section>
  );
};

// ─── What I Do ────────────────────────────────────────────────────────────────
const WHAT_I_DO = [
  { cmd: "whoami --role cybersecurity", label: "Cybersecurity", points: ["Vulnerability assessment (Nmap, Burp Suite, DVWA)", "Blue Team: Kibana ELK, Wazuh SIEM, Wireshark, Nessus", "CTF labs: recon, enumeration, basic exploitation", "Risk-based prioritization & reproducible reporting"] },
  { cmd: "whoami --role noc", label: "Network (NOC L1)", points: ["Real-time monitoring: Grafana, OpManager, Dynatrace, Elastic", "Incident triage & escalation to L2/L3 — 24×7 rotational shift", "UiPath automation for NOC report generation", "Excel trend mapping & Jira ticketing"] },
  { cmd: "whoami --role dev", label: "Full-Stack Dev", points: ["React.js front-end with responsive, accessible UIs", "Express + MySQL backend with REST API", "Functional, regression & performance QA", "Multer uploads, JWT auth, analytics tracking"] },
];

const WhatIDoSection = ({ profile }) => {
  if (profile?.show_what_i_do === 0) return null;
  return (
    <section id="whatido" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHeader cmd="cat what_i_do.md" />
        <div className="grid md:grid-cols-3 gap-4">
          {WHAT_I_DO.map((item) => (
            <TermWindow key={item.label} title={item.cmd}>
              <p className="text-green-500 text-xs font-bold mb-2">[{item.label}]</p>
              <ul className="space-y-1.5">
                {item.points.map((pt, i) => (
                  <li key={i} className="text-xs text-green-300 flex gap-2">
                    <span className="text-green-700 shrink-0">+</span>{pt}
                  </li>
                ))}
              </ul>
            </TermWindow>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Skills ───────────────────────────────────────────────────────────────────
const bar = (level) => {
  const filled = Math.round((Math.min(level ?? 0, 100) / 100) * 16);
  return "█".repeat(filled) + "░".repeat(16 - filled);
};

const SkillsSection = ({ skills }) => {
  if (!skills.length) return null;
  const hard = skills.filter((s) => s.category === "hard");
  const soft = skills.filter((s) => s.category === "soft");

  return (
    <section id="skills" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHeader cmd="./scan_skills.sh --all" />
        <div className="grid md:grid-cols-2 gap-6">
          {[["hard_skills", hard], ["soft_skills", soft]].map(([label, data]) =>
            data.length > 0 ? (
              <TermWindow key={label} title={`${label}.log`}>
                <p className="text-green-700 text-xs mb-3">[+] scanning {label}...</p>
                <div className="space-y-2">
                  {data.map((s) => (
                    <div key={s.id} className="text-xs">
                      <div className="flex justify-between text-green-400">
                        <span>{s.name}</span>
                        {s.level != null && <span className="text-green-600">{s.level}%</span>}
                      </div>
                      {s.level != null && (
                        <span className="text-green-500 tracking-tighter">[{bar(s.level)}]</span>
                      )}
                    </div>
                  ))}
                </div>
              </TermWindow>
            ) : null
          )}
        </div>
      </div>
    </section>
  );
};

// ─── Experience ───────────────────────────────────────────────────────────────
const ExperienceSection = ({ experience }) => {
  if (!experience.length) return null;
  const sorted = sortExperience(experience);
  return (
    <section id="experience" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHeader cmd="git log --oneline ./experience" />
        <div className="space-y-4">
          {sorted.map((exp, i) => {
            const points = Array.isArray(exp.points) ? exp.points : (typeof exp.points === "string" ? (() => { try { return JSON.parse(exp.points || "[]"); } catch { return []; } })() : []);
            return (
              <TermWindow key={exp.id ?? i} title={`commit @ ${exp.company}`}>
                <p className="text-yellow-500/80 text-xs">
                  commit {(exp.id ?? i).toString().padStart(7, "0")}a{i}f
                </p>
                <p className="text-green-400 text-sm font-bold mt-1">{exp.role}</p>
                <p className="text-green-600 text-xs">
                  <span className="text-green-800">@</span> {exp.company}
                  {exp.employment_type && <><span className="text-green-800"> · </span><span className="text-green-700">{exp.employment_type}</span></>}
                  <span className="text-green-800"> · </span>
                  {exp.start_date} — {exp.is_current ? "Present" : exp.end_date}
                  {calcDuration(exp.start_date, exp.end_date, exp.is_current) && (
                    <><span className="text-green-800"> · </span><span className="text-green-800">{calcDuration(exp.start_date, exp.end_date, exp.is_current)}</span></>
                  )}
                </p>
                {points.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {points.map((pt, j) => (
                      <li key={j} className="text-green-300 text-xs flex gap-2">
                        <span className="text-green-700">+</span>{pt}
                      </li>
                    ))}
                  </ul>
                )}
                <ExperienceMediaCards media={exp.media} cardStyle="terminal" />
              </TermWindow>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Education ────────────────────────────────────────────────────────────────
const EducationSection = ({ education }) => {
  if (!education.length) return null;
  return (
    <section id="education" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHeader cmd="cat education.json" />
        <div className="grid md:grid-cols-2 gap-6">
          {education.map((edu, i) => (
            <TermWindow key={edu.id ?? i} title="education.json">
              <pre className="text-xs text-green-400 whitespace-pre-wrap leading-relaxed">
<span className="text-green-700">{"{"}</span>{"\n"}
{"  "}<span className="text-green-600">"school"</span>: <span className="text-green-300">"{edu.institution}"</span>,{"\n"}
{edu.degree && <>{"  "}<span className="text-green-600">"degree"</span>: <span className="text-green-300">"{edu.degree}"</span>,{"\n"}</>}
{edu.field && <>{"  "}<span className="text-green-600">"field"</span>: <span className="text-green-300">"{edu.field}"</span>,{"\n"}</>}
{"  "}<span className="text-green-600">"years"</span>: <span className="text-green-300">"{edu.start_year}–{edu.end_year || "now"}"</span>{"\n"}
<span className="text-green-700">{"}"}</span>
              </pre>
              {edu.description && <p className="text-green-600 text-xs mt-2">{"// "}{edu.description}</p>}
            </TermWindow>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Blog ─────────────────────────────────────────────────────────────────────
const BlogSection = ({ posts }) => {
  if (!posts.length) return null;
  return (
    <section id="blog" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-8">
          <SectionHeader cmd="ls -la ./blog/" />
          <Link to="/blog" className="text-xs text-green-500 hover:text-green-400 font-mono mb-1">
            cd ./blog &gt;_
          </Link>
        </div>
        <TermWindow title="affan@portfolio: ~/blog">
          <p className="text-green-700 text-xs mb-3">total {posts.length}</p>
          <div className="space-y-1">
            {posts.slice(0, 8).map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group flex items-center gap-3 text-xs hover:bg-green-500/5 px-2 py-1.5 rounded transition-colors"
              >
                <span className="text-green-800 hidden sm:inline">-rw-r--r--</span>
                <span className="text-green-700 hidden sm:inline">
                  {post.published_at ? new Date(post.published_at).toLocaleDateString("en-CA") : "----"}
                </span>
                <span className="text-green-400 group-hover:text-green-300 truncate flex-1">
                  {post.title}
                </span>
                <span className="text-green-800 opacity-0 group-hover:opacity-100 transition-opacity">
                  cat &gt;_
                </span>
              </Link>
            ))}
          </div>
        </TermWindow>
      </div>
    </section>
  );
};

// ─── Portfolio ────────────────────────────────────────────────────────────────
const PortfolioSection = ({ projects }) => {
  const [showAll, setShowAll] = useState(false);
  if (!projects.length) return null;
  const visible = showAll ? projects : projects.slice(0, 6);
  return (
    <section id="portfolio" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHeader cmd="ls -la ./portfolio/" />
        <TermWindow title="affan@portfolio: ~/portfolio">
          <p className="text-green-700 text-xs mb-3">total {projects.length} projects</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {visible.map((proj, i) => (
              <a
                key={proj.id ?? i}
                href={proj.link || undefined}
                target={proj.link ? "_blank" : undefined}
                rel="noopener noreferrer"
                className={`block border border-green-900/40 rounded p-3 transition-colors ${proj.link ? "hover:border-green-500/40 hover:bg-green-500/5 cursor-pointer" : "cursor-default"}`}
              >
                <p className="text-green-300 text-xs font-mono font-bold leading-snug">{proj.name}</p>
                {proj.category && <p className="text-green-700 text-xs mt-0.5">+ type: {proj.category}</p>}
                {proj.link && <p className="text-green-600 text-xs mt-1">→ open</p>}
              </a>
            ))}
          </div>
          {projects.length > 6 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-4 text-xs text-green-600 hover:text-green-400 font-mono transition-colors"
            >
              {showAll ? "$ collapse --all" : `$ ls --all  # +${projects.length - 6} more`}
            </button>
          )}
        </TermWindow>
      </div>
    </section>
  );
};

// ─── Certificates ─────────────────────────────────────────────────────────────
const CertificateSection = ({ certificates }) => {
  const [showAll, setShowAll] = useState(false);
  if (!certificates.length) return null;
  const visible = showAll ? certificates : certificates.slice(0, 6);
  return (
    <section id="certificates" className="py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHeader cmd="ls -la ./certificates/" />
        <TermWindow title="affan@portfolio: ~/certificates">
          <p className="text-green-700 text-xs mb-3">total {certificates.length} certs</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {visible.map((cert, i) => (
              <a
                key={cert.id ?? i}
                href={cert.link || undefined}
                target={cert.link ? "_blank" : undefined}
                rel="noopener noreferrer"
                className={`block border border-green-900/40 rounded p-3 transition-colors ${cert.link ? "hover:border-green-500/40 hover:bg-green-500/5 cursor-pointer" : "cursor-default"}`}
              >
                <p className="text-green-300 text-xs font-mono font-bold leading-snug">{cert.name}</p>
                {cert.organizer && <p className="text-green-700 text-xs mt-0.5">+ issuer: {cert.organizer}</p>}
                {cert.category && <p className="text-green-800 text-xs mt-0.5">+ type: {cert.category}</p>}
                {cert.link && <p className="text-green-600 text-xs mt-1">→ verify</p>}
              </a>
            ))}
          </div>
          {certificates.length > 6 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-4 text-xs text-green-600 hover:text-green-400 font-mono transition-colors"
            >
              {showAll ? "$ collapse --all" : `$ ls --all  # +${certificates.length - 6} more`}
            </button>
          )}
        </TermWindow>
      </div>
    </section>
  );
};

// ─── Contact ──────────────────────────────────────────────────────────────────
const ContactSection = () => (
  <section id="contact" className="py-20">
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      <SectionHeader cmd="./contact.sh" />
      <TermWindow title="contact.sh — output">
        <p className="text-green-300 text-sm"><span className="text-green-700">$ </span>echo "Want to collaborate? Ping me."</p>
        <p className="text-green-300 text-sm mt-1">&gt; Want to collaborate? Ping me.</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-5">
          <a
            href={`https://wa.me/628997779254?text=${encodeURIComponent("Halo Affan, saya melihat portfolio kamu dan ingin terhubung.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500/10 border border-green-500/50 hover:bg-green-500/20 hover:border-green-400 text-green-400 font-bold rounded text-sm transition-all duration-150"
          >
            <span className="text-green-700">$</span> ./whatsapp.sh
          </a>
          <a
            href={`mailto:affanhaidar232@gmail.com?subject=${encodeURIComponent("Hi Affan — from your portfolio")}&body=${encodeURIComponent("Halo Affan,\n\nSaya melihat portfolio kamu dan ingin terhubung.\n\n")}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500/10 border border-green-500/50 hover:bg-green-500/20 hover:border-green-400 text-green-400 font-bold rounded text-sm transition-all duration-150"
          >
            <span className="text-green-700">$</span> mail affanhaidar232@gmail.com
          </a>
        </div>
      </TermWindow>
    </div>
  </section>
);

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="border-t border-green-500/20 py-6 text-center font-mono">
    <p className="text-green-700 text-xs">
      <span className="text-green-800">$ </span>
      echo "© {new Date().getFullYear()} Affan Haidar Atstsabit"
      <span className="ml-1 inline-block w-2 h-3.5 bg-green-600 align-middle animate-pulse" />
    </p>
  </footer>
);

// ─── Intersection observer ────────────────────────────────────────────────────
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const observers = ids.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { threshold: 0.4 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, [ids]);
  return active;
}

// ─── Root ─────────────────────────────────────────────────────────────────────
const TerminalTheme = () => {
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [education, setEducation] = useState([]);
  const [experience, setExperience] = useState([]);
  const [posts, setPosts] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [projects, setProjects] = useState([]);

  const sectionIds = ["home", "about", "skills", "experience", "education", "portfolio", "certificates", "blog", "contact"];
  const activeSection = useActiveSection(sectionIds);

  useEffect(() => {
    trackVisit("/");
    Promise.all([getProfile(), getSkills(), getEducation(), getExperience(), getPublishedPosts(), fetchCertificates(), fetchPortofolios()])
      .then(([prof, sk, edu, exp, bl, certs, projs]) => {
        setProfile(prof.profile ?? prof);
        setSkills(Array.isArray(sk) ? sk : []);
        setEducation(Array.isArray(edu) ? edu : []);
        setExperience(Array.isArray(exp) ? exp : []);
        setPosts(Array.isArray(bl) ? bl : []);
        setCertificates(Array.isArray(certs) ? certs : []);
        setProjects(Array.isArray(projs) ? projs : []);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-black text-green-400 relative overflow-hidden">
      {/* CRT scanline overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-40 opacity-[0.07]"
        style={{
          backgroundImage: "repeating-linear-gradient(0deg, #22c55e 0px, #22c55e 1px, transparent 1px, transparent 3px)",
        }}
      />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(0,0,0,0.6)_100%)]" />

      <Navbar activeSection={activeSection} />
      <HeroSection profile={profile} />
      <AboutSection profile={profile} />
      <WhatIDoSection profile={profile} />
      <SkillsSection skills={skills} />
      <ExperienceSection experience={experience} />
      <EducationSection education={education} />
      <PortfolioSection projects={projects} />
      <CertificateSection certificates={certificates} />
      <BlogSection posts={posts} />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default TerminalTheme;
